import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Clock, User, Phone, Calendar, Loader2, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MisApartados() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [apartados, setApartados] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const authRes = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
        if (!authRes.ok) { navigate('/login'); return; }
        const res = await fetch(`${BACKEND_URL}/api/comisionista/mis-apartados`, { credentials: 'include' });
        if (res.ok) setApartados(await res.json());
        else toast.error('Error al cargar apartados');
      } catch { toast.error('Error de conexión'); }
      finally { setLoading(false); }
    };
    load();
  }, [navigate]);

  const getTimeRemaining = (expira) => {
    if (!expira) return null;
    const diff = new Date(expira) - new Date();
    if (diff <= 0) return 'Expirado';
    const hours = Math.floor(diff / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    return `${hours}h ${mins}m`;
  };

  if (loading) {
    return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#C6A54A] animate-spin" /></div>;
  }

  return (
    <div data-testid="mis-apartados-page" className="p-4 sm:p-6 space-y-5 max-w-[1000px] mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <MapPin className="w-6 h-6 text-[#C6A54A]" />
          Mis Apartados
        </h1>
        <p className="text-sm text-zinc-500 mt-1">Lotes que tienes apartados actualmente</p>
      </div>

      <div className="bg-[#C6A54A]/10 rounded-xl p-4 border border-zinc-800/50">
        <p className="text-xs text-zinc-500 uppercase tracking-wider">Apartados Activos</p>
        <p className="text-3xl font-bold text-[#C6A54A] mt-1">{apartados.length}</p>
      </div>

      {apartados.length === 0 ? (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-12 text-center">
          <MapPin className="w-14 h-14 text-zinc-700 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Sin apartados activos</h3>
          <p className="text-sm text-zinc-500">Ve a la sección de Lotes para apartar uno</p>
        </div>
      ) : (
        <div className="space-y-3">
          {apartados.map(lote => {
            const timeLeft = getTimeRemaining(lote.apartado_expira);
            const isUrgent = timeLeft && !timeLeft.includes('Expirado') && parseInt(timeLeft) < 6;
            return (
              <div key={lote.lote_id} data-testid={`apartado-${lote.lote_id}`}
                className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg font-bold text-white">Lote #{lote.numero}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[#C6A54A]/20 text-[#C6A54A]">Mz {lote.manzana}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-400">
                      <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" />{lote.fraccionamiento_nombre}</span>
                      {lote.cliente_nombre && <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" />{lote.cliente_nombre}</span>}
                      {lote.cliente_telefono && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{lote.cliente_telefono}</span>}
                      {lote.firma_programada && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          Firma: {new Date(lote.firma_programada).toLocaleDateString('es-MX')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-lg font-bold text-[#C6A54A]">${lote.precio?.toLocaleString('es-MX')}</p>
                    <p className="text-xs text-zinc-500">{lote.moneda || 'USD'}</p>
                    {timeLeft && (
                      <div className={`mt-1 flex items-center gap-1 text-xs ${isUrgent ? 'text-red-400' : 'text-zinc-400'}`}>
                        <Clock className="w-3 h-3" />{timeLeft}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
